import { useMemo, useRef, useState } from 'react'
import { nombreFr } from './lib.js'

/**
 * COURBE INSIGHTS — un point = un RELEVÉ RÉEL (§67). Aucun lissage qui
 * inventerait des intermédiaires : la ligne relie les points existants, les
 * jours sans relevé restent des trous visibles. Reveal court au montage
 * (stroke-dashoffset, courbe des tokens Motion), tooltip au survol avec
 * date, valeur, delta et provenance.
 */

export interface ChartPoint {
  day: string
  value: number
  provenance?: string
}

const PROVENANCE_FR: Record<string, string> = {
  GESTION_SNAPSHOT: 'Relevé Gestion',
  PROVIDER_BACKFILL: 'Historique Instagram (importé)',
}

export function InsightsChart({ points, height = 160, accent = 'var(--violet)' }: {
  points: ChartPoint[]
  height?: number
  accent?: string
}) {
  const [survol, setSurvol] = useState<number | null>(null)
  const ref = useRef<SVGSVGElement>(null)
  const W = 600
  const H = height
  const PAD = 14

  const calc = useMemo(() => {
    if (points.length === 0) return null
    const vals = points.map((p) => p.value)
    const min = Math.min(...vals)
    const max = Math.max(...vals)
    const span = max - min || Math.max(1, max * 0.05)
    const t0 = new Date(points[0]!.day).getTime()
    const t1 = new Date(points[points.length - 1]!.day).getTime()
    const dt = Math.max(1, t1 - t0)
    const xy = points.map((p) => ({
      ...p,
      x: PAD + ((new Date(p.day).getTime() - t0) / dt) * (W - 2 * PAD),
      y: H - PAD - ((p.value - min) / span) * (H - 2 * PAD - 18),
    }))
    return { xy, min, max }
  }, [points, H])

  if (!calc) return <p className="lc-hint">Aucun relevé pour l’instant.</p>
  if (points.length === 1) {
    return (
      <p className="lc-hint">
        Un seul relevé ({nombreFr(points[0]!.value)} le {new Date(points[0]!.day).toLocaleDateString('fr-FR')}) —
        la courbe se dessinera avec les prochains jours.
      </p>
    )
  }

  const d = calc.xy.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const aire = `${d} L${calc.xy[calc.xy.length - 1]!.x.toFixed(1)},${H - PAD} L${calc.xy[0]!.x.toFixed(1)},${H - PAD} Z`
  const actif = survol !== null ? calc.xy[survol]! : null
  const precedent = survol !== null && survol > 0 ? calc.xy[survol - 1]! : null

  const surMouvement = (e: React.PointerEvent) => {
    const svg = ref.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * W
    let meilleur = 0
    let distance = Infinity
    calc.xy.forEach((p, i) => {
      const dx = Math.abs(p.x - x)
      if (dx < distance) { distance = dx; meilleur = i }
    })
    setSurvol(meilleur)
  }

  return (
    <div className="ig-chart">
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        className="ig-chart__svg" role="img" aria-label="Courbe des relevés"
        onPointerMove={surMouvement} onPointerLeave={() => setSurvol(null)}>
        <defs>
          <linearGradient id="ig-aire" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.22" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={aire} fill="url(#ig-aire)" className="ig-chart__aire" />
        <path d={d} fill="none" stroke={accent} strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" className="ig-chart__ligne" />
        {calc.xy.map((p, i) => (
          <circle key={p.day} cx={p.x} cy={p.y}
            r={survol === i ? 5 : calc.xy.length <= 45 ? 2.6 : 0}
            fill={p.provenance === 'PROVIDER_BACKFILL' ? 'var(--cyan)' : accent}
            className="ig-chart__point" />
        ))}
        {actif ? <line x1={actif.x} y1={PAD} x2={actif.x} y2={H - PAD}
          stroke="currentColor" strokeOpacity="0.18" strokeDasharray="3 3" /> : null}
      </svg>
      {actif ? (
        <div className="ig-chart__tip" style={{ left: `${(actif.x / W) * 100}%` }}>
          <strong>{nombreFr(actif.value)}</strong>
          {precedent ? (
            <span className={actif.value >= precedent.value ? 'ig-chart__tip-up' : 'ig-chart__tip-down'}>
              {actif.value >= precedent.value ? '+' : '−'}{nombreFr(Math.abs(actif.value - precedent.value))}
            </span>
          ) : null}
          <em>{new Date(actif.day).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
            {actif.provenance ? ` · ${PROVENANCE_FR[actif.provenance] ?? actif.provenance}` : ''}</em>
        </div>
      ) : null}
    </div>
  )
}
